import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  getMarketNews,
  getStockCorporateActions,
  getStockNews,
  getStockReportedQuarter,
} from "../marketData/marketDataApi";
import {
  createNinaAssistantResponse,
  createNinaConversation,
  createNinaUserMessage,
  listNinaConversations,
  listNinaMessages,
} from "../nina/ninaConversationApi";
import "./AskNinaWorkspace.css";

function AskNinaWorkspace({
  liveDecision,
  guestMode = false,
}) {
  const [selectedSymbol, setSelectedSymbol] = useState(() => {
    try {
      return localStorage.getItem("aninaSelectedSymbol") || "PLUG";
    } catch (_error) {
      return "PLUG";
    }
  });
  const [newsState, setNewsState] = useState({
  status: "idle",
  articles: [],
  error: "",
});

const [marketNewsState, setMarketNewsState] = useState({
  status: "idle",
  articles: [],
  error: "",
});

const [corporateActionsState, setCorporateActionsState] = useState({
  status: "idle",
  actions: {},
  error: "",
});

const [reportedQuarterState, setReportedQuarterState] = useState({
  status: "idle",
  quarter: null,
  error: "",
});

const [conversationState, setConversationState] = useState({
  status: "idle",
  conversations: [],
  error: "",
});

const [conversationCreateState, setConversationCreateState] = useState({
  status: "idle",
  error: "",
});

const [activeConversation, setActiveConversation] = useState(null);

const [messageState, setMessageState] = useState({
  status: "idle",
  messages: [],
  error: "",
});

const [messageDraft, setMessageDraft] = useState("");

const [messageSendState, setMessageSendState] = useState({
  status: "idle",
  error: "",
});

  useEffect(() => {
    const handleSymbolChange = (event) => {
      const symbol = String(event.detail?.symbol || "")
        .trim()
        .toUpperCase();

      if (symbol) {
        setSelectedSymbol(symbol);
      }
    };

    window.addEventListener(
      "nina:symbol-change",
      handleSymbolChange
    );

    return () => {
      window.removeEventListener(
        "nina:symbol-change",
        handleSymbolChange
      );
    };
  }, []);

  useEffect(() => {
  if (guestMode) return undefined;

  let cancelled = false;

  setNewsState({
    status: "loading",
    articles: [],
    error: "",
  });

  getStockNews(selectedSymbol, 6)
    .then((payload) => {
      if (cancelled) return;

      setNewsState({
        status: "ready",
        articles: Array.isArray(payload?.news) ? payload.news : [],
        error: "",
      });
    })
    .catch((error) => {
      if (cancelled) return;

      setNewsState({
        status: "error",
        articles: [],
        error: String(
          error?.message || "Authenticated news is unavailable.",
        ),
      });
    });

  return () => {
    cancelled = true;
  };
}, [selectedSymbol, guestMode]);

useEffect(() => {
  if (guestMode) return undefined;

  let cancelled = false;

  setReportedQuarterState({
    status: "loading",
    quarter: null,
    error: "",
  });

  getStockReportedQuarter(selectedSymbol)
    .then((payload) => {
      if (cancelled) return;

      setReportedQuarterState({
        status: "ready",
        quarter: payload?.reported_quarter || null,
        error: "",
      });
    })
    .catch((error) => {
      if (cancelled) return;

      setReportedQuarterState({
        status: "error",
        quarter: null,
        error: String(
          error?.message || "SEC/EDGAR quarter data is unavailable.",
        ),
      });
    });

  return () => {
    cancelled = true;
  };
}, [selectedSymbol, guestMode]);

useEffect(() => {
  if (guestMode) return undefined;

  let cancelled = false;

  setCorporateActionsState({
    status: "loading",
    actions: {},
    error: "",
  });

  getStockCorporateActions(selectedSymbol)
    .then((payload) => {
      if (cancelled) return;

      setCorporateActionsState({
        status: "ready",
        actions:
          payload?.corporate_actions &&
          typeof payload.corporate_actions === "object"
            ? payload.corporate_actions
            : {},
        error: "",
      });
    })
    .catch((error) => {
      if (cancelled) return;

      setCorporateActionsState({
        status: "error",
        actions: {},
        error: String(
          error?.message ||
            "Authenticated corporate actions are unavailable.",
        ),
      });
    });

  return () => {
    cancelled = true;
  };
}, [selectedSymbol, guestMode]);

useEffect(() => {
  if (guestMode) return undefined;

  let cancelled = false;

  setConversationState({
    status: "loading",
    conversations: [],
    error: "",
  });

  listNinaConversations()
    .then((payload) => {
      if (cancelled) return;

      setConversationState({
        status: "ready",
        conversations: Array.isArray(payload?.conversations)
          ? payload.conversations
          : [],
        error: "",
      });
    })
    .catch((error) => {
      if (cancelled) return;

      setConversationState({
        status: "error",
        conversations: [],
        error: String(
          error?.message || "Saved conversations are unavailable.",
        ),
      });
    });

  return () => {
    cancelled = true;
  };
}, [guestMode]);

const createConversation = async () => {
  if (guestMode || conversationCreateState.status === "creating") return;

  setConversationCreateState({
    status: "creating",
    error: "",
  });

  try {
    const payload = await createNinaConversation(null);
    const conversation = payload?.conversation;

    if (!conversation?.id) {
      throw new Error("The new conversation could not be opened.");
    }

    setConversationState((current) => ({
      status: "ready",
      conversations: [
        conversation,
        ...current.conversations.filter(
          (item) => item.id !== conversation.id,
        ),
      ],
      error: "",
    }));
    setActiveConversation(conversation);
    setMessageDraft("");
    setMessageState({
      status: "ready",
      messages: [],
      error: "",
    });
    setMessageSendState({
      status: "idle",
      error: "",
    });
    setConversationCreateState({
      status: "idle",
      error: "",
    });
  } catch (error) {
    setConversationCreateState({
      status: "error",
      error: String(
        error?.message || "A new conversation could not be created.",
      ),
    });
  }
};

const openConversation = async (conversation) => {
  if (guestMode || !conversation?.id) return;

  setActiveConversation(conversation);
  setMessageDraft("");
  setMessageSendState({
    status: "idle",
    error: "",
  });
  setMessageState({
    status: "loading",
    messages: [],
    error: "",
  });

  try {
    const payload = await listNinaMessages(conversation.id);

    setMessageState({
      status: "ready",
      messages: Array.isArray(payload?.messages) ? payload.messages : [],
      error: "",
    });
  } catch (error) {
    setMessageState({
      status: "error",
      messages: [],
      error: String(
        error?.message || "Conversation messages are unavailable.",
      ),
    });
  }
};

const sendUserMessage = async (event) => {
  event.preventDefault();

  if (guestMode || !activeConversation?.id) return;

  const content = messageDraft.trim();
  if (!content) return;

  setMessageSendState({
    status: "sending",
    error: "",
  });

  try {
    const userPayload = await createNinaUserMessage(
      activeConversation.id,
      content,
    );
    const userMessage = userPayload?.message;

    setMessageDraft("");
    setMessageState((current) => ({
      status: "ready",
      messages: [
        ...(Array.isArray(current.messages) ? current.messages : []),
        ...(userMessage ? [userMessage] : []),
      ],
      error: "",
    }));
    setMessageSendState({
      status: "responding",
      error: "",
    });

    try {
      const responsePayload = await createNinaAssistantResponse(
        activeConversation.id,
      );
      const assistantMessage = responsePayload?.message;

      setMessageState((current) => ({
        status: "ready",
        messages: [
          ...(Array.isArray(current.messages) ? current.messages : []),
          ...(assistantMessage ? [assistantMessage] : []),
        ],
        error: "",
      }));
    } catch (responseError) {
      const payload = await listNinaMessages(activeConversation.id);

      setMessageState({
        status: "ready",
        messages: Array.isArray(payload?.messages) ? payload.messages : [],
        error: "",
      });
      setMessageSendState({
        status: "error",
        error: String(
          responseError?.message ||
            "Your message was saved, but the AI Assistant response is unavailable.",
        ),
      });
      return;
    }
    setMessageSendState({
      status: "idle",
      error: "",
    });
  } catch (error) {
    setMessageSendState({
      status: "error",
      error: String(
        error?.message || "Your message could not be saved.",
      ),
    });
  }
};

useEffect(() => {
  if (guestMode) return undefined;

  let cancelled = false;

  setMarketNewsState({
    status: "loading",
    articles: [],
    error: "",
  });

  getMarketNews(6)
    .then((payload) => {
      if (cancelled) return;

      setMarketNewsState({
        status: "ready",
        articles: Array.isArray(payload?.news) ? payload.news : [],
        error: "",
      });
    })
    .catch((error) => {
      if (cancelled) return;

      setMarketNewsState({
        status: "error",
        articles: [],
        error: String(
          error?.message || "Authenticated market news is unavailable.",
        ),
      });
    });

  return () => {
    cancelled = true;
  };
}, [guestMode]);

const latestNewsArticle = newsState.articles[0] || null;
const latestMarketNewsArticle =
  marketNewsState.articles[0] || null;
const latestEarningsArticle =
  newsState.articles.find((article) =>
    /\b(earnings|quarter|quarterly|q[1-4]|eps|revenue|guidance|results)\b/i.test(
      `${article?.headline || ""}`,
    ),
  ) || null;
const reportedQuarter = reportedQuarterState.quarter;

const formatFinancialValue = (metric, kind) => {
  const value = Number(metric?.value);
  if (!Number.isFinite(value)) return "Unavailable";
  if (kind === "eps") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
};
  const corporateActionEntries = Object.entries(
  corporateActionsState.actions || {},
).flatMap(([actionType, items]) =>
  Array.isArray(items)
    ? items.map((item) => ({
        ...item,
        actionType,
      }))
    : [],
);

const latestCorporateAction =
  corporateActionEntries.find((action) =>
    /split/i.test(action.actionType),
  ) ||
  corporateActionEntries[0] ||
  null;

  const decisionText = String(
    liveDecision?.decision || ""
  ).toUpperCase();

  const decisionStatus = String(
    liveDecision?.status || ""
  ).toUpperCase();

  let ninaSignal = {
    color: "yellow",
    label: "WAIT",
    action: "Wait for authenticated confirmation",
  };

  if (decisionText.includes("BEARISH")) {
    ninaSignal = {
      color: "red",
      label: "TREND DOWN / AVOID",
      action: "Protect capital and do not enter",
    };
  } else if (decisionText.includes("WATCH LONG SETUP")) {
    ninaSignal = {
      color: "green",
      label: "TREND UP",
      action: "Set Buy Plan",
    };
  } else if (
    decisionText.includes("DO NOT ENTER") ||
    decisionStatus === "BLOCKED" ||
    decisionStatus === "DATA BLOCKED"
  ) {
    ninaSignal = {
      color: "yellow",
      label: "WAIT",
      action: "Conditions are not ready for entry",
    };
  }

  if (guestMode) {
    return (
      <section aria-labelledby="ask-nina-title">
        <p>AninaTrade AI Assistant</p>

        <h1 id="ask-nina-title">Ask AI Assistant</h1>

        <p>
          Active symbol: <strong>{selectedSymbol}</strong>
        </p>

        <section
          className="askNinaIntelligence"
          aria-labelledby="nina-guest-title"
        >
          <h2 id="nina-guest-title">
            Guest Explore Mode
          </h2>

          <p>
            Explore the AninaTrade AI Assistant safely. Sign in or create a free account
            to unlock authenticated market intelligence, saved
            conversations, and personalized AI memory.
          </p>
        </section>
      </section>
    );
  }

  return (
    <section aria-labelledby="ask-nina-title">
      <p>AninaTrade AI Assistant</p>

      <h1 id="ask-nina-title">Ask AI Assistant</h1>

      <p>
        Active symbol: <strong>{selectedSymbol}</strong>
      </p>

      <div
  className={`askNinaDecision askNinaDecision--${ninaSignal.color}`}
  aria-label={`AI Assistant trend color ${ninaSignal.color}`}
>
 <div className="askNinaDecisionLight" aria-hidden="true" />

{ninaSignal.color === "yellow" && (
  <strong className="askNinaHoldLabel">HOLD</strong>
)}
</div>

      <section
        className="askNinaConversations"
        aria-labelledby="nina-conversations-title"
      >
        <div className="askNinaConversationsHeader">
          <div>
            <span>AUTHENTICATED HISTORY</span>
            <h2 id="nina-conversations-title">Saved Conversations</h2>
          </div>
          <button
            type="button"
            className="askNinaNewConversationButton"
            onClick={createConversation}
            disabled={conversationCreateState.status === "creating"}
          >
            {conversationCreateState.status === "creating"
              ? "Creating..."
              : "New Conversation"}
          </button>
        </div>

        {conversationCreateState.status === "error" ? (
          <p role="alert">{conversationCreateState.error}</p>
        ) : null}

        {conversationState.status === "loading" ? (
          <p>Loading saved conversations...</p>
        ) : conversationState.status === "error" ? (
          <p role="alert">{conversationState.error}</p>
        ) : conversationState.conversations.length ? (
          <ul className="askNinaConversationList">
            {conversationState.conversations.map((conversation) => (
              <li key={conversation.id}>
                <button
                  type="button"
                  onClick={() => openConversation(conversation)}
                  aria-pressed={activeConversation?.id === conversation.id}
                >
                  <strong>{conversation.title || "New conversation"}</strong>
                  <small>{conversation.updated_at || conversation.created_at || ""}</small>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p>No saved conversations yet.</p>
        )}

        {activeConversation ? (
          <section
            className="askNinaConversationMessages"
            aria-labelledby="nina-active-conversation-title"
          >
            <h3 id="nina-active-conversation-title">
              {activeConversation.title || "New conversation"}
            </h3>

            {messageState.status === "loading" ? (
              <p>Loading conversation...</p>
            ) : messageState.status === "error" ? (
              <p role="alert">{messageState.error}</p>
            ) : messageState.messages.length ? (
              <ol className="askNinaMessageList">
                {messageState.messages.map((message) => (
                  <li
                    key={message.id}
                    className={`askNinaMessage askNinaMessage--${message.role}`}
                  >
                    <span>
                      {message.role === "assistant" ? "AI Assistant" : "You"}
                    </span>
                    {message.role === "assistant" ? (
                      <div className="askNinaMarkdown">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p>{message.content}</p>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p>No messages in this conversation yet.</p>
            )}

            <form
              className="askNinaMessageComposer"
              onSubmit={sendUserMessage}
            >
              <label htmlFor="ask-nina-message">
                Message AninaTrade AI Assistant
              </label>
              <div>
                <textarea
                  id="ask-nina-message"
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  placeholder="Type your message..."
                  rows="3"
                  maxLength="40000"
                  disabled={
                    messageState.status === "loading" ||
                    messageSendState.status === "sending" ||
                    messageSendState.status === "responding"
                  }
                />
                <button
                  type="submit"
                  disabled={
                    !messageDraft.trim() ||
                    messageState.status === "loading" ||
                    messageSendState.status === "sending" ||
                    messageSendState.status === "responding"
                  }
                >
                  {messageSendState.status === "sending"
                    ? "Saving..."
                    : messageSendState.status === "responding"
                      ? "AI Responding..."
                      : "Send Message"}
                </button>
              </div>
              {messageSendState.status === "error" ? (
                <p role="alert">{messageSendState.error}</p>
              ) : null}
            </form>
          </section>
        ) : null}
      </section>

      <section
        className="askNinaIntelligence"
        aria-labelledby="nina-intelligence-title"
      >
        <h2 id="nina-intelligence-title">
          AI Market Intelligence
        </h2>

        <p>
          Authenticated information only. The AninaTrade AI Assistant will not invent
          market events or company news.
        </p>

        <div className="askNinaIntelligenceGrid">
 <article className="askNinaIntelCard">
  <span>COMPANY EVENT</span>
  <h3>Quarter & Earnings</h3>
  {reportedQuarterState.status === "loading" ? (
    <p>Checking SEC/EDGAR reported-quarter data...</p>
  ) : reportedQuarter ? (
    <div className="askNinaQuarterFacts">
      <p>
        <strong>
          {reportedQuarter.fiscal_period || "Quarter"}
          {reportedQuarter.fiscal_year
            ? ` FY${reportedQuarter.fiscal_year}`
            : ""}
        </strong>
        {reportedQuarter.period_end
          ? ` · Period ended ${reportedQuarter.period_end}`
          : ""}
      </p>
      <p>
        Revenue: {formatFinancialValue(reportedQuarter.revenue, "revenue")}
        {" · "}Diluted EPS: {formatFinancialValue(reportedQuarter.diluted_eps, "eps")}
      </p>
      <p>
        Filed {reportedQuarter.filed_date || "date unavailable"}
        {reportedQuarter.form ? ` (${reportedQuarter.form})` : ""}
        {" · Source: SEC/EDGAR"}
      </p>
      <p>
        Earnings news: {newsState.status === "loading"
          ? "Checking authenticated news..."
          : newsState.status === "error"
            ? "Authenticated news unavailable"
            : latestEarningsArticle?.headline ||
              "No recent authenticated earnings news found"}
      </p>
    </div>
  ) : (
    <p>
      {reportedQuarterState.status === "error"
        ? "SEC/EDGAR reported-quarter data unavailable. "
        : "No structured SEC reported-quarter facts found. "}
      {newsState.status === "loading"
        ? "Checking authenticated earnings news..."
        : newsState.status === "error"
          ? "Authenticated earnings news unavailable"
          : latestEarningsArticle?.headline ||
            "No recent authenticated earnings news found"}
    </p>
  )}
</article>


         <article className="askNinaIntelCard">
  <span>CORPORATE ACTIONS</span>
  <h3>Stock Splits & Corporate Actions</h3>
  <p>
    {corporateActionsState.status === "loading"
      ? "Checking authenticated corporate actions..."
      : corporateActionsState.status === "error"
        ? "Authenticated corporate action information unavailable"
        : latestCorporateAction
          ? `${latestCorporateAction.actionType
              .replaceAll("_", " ")
              .toUpperCase()} — ${
              latestCorporateAction.ex_date ||
              latestCorporateAction.process_date ||
              latestCorporateAction.record_date ||
              "Date unavailable"
            }`
          : "No authenticated corporate actions found"}
  </p>
</article>


          <article className="askNinaIntelCard">
            <span>SYMBOL NEWS</span>
            <h3>{selectedSymbol} News</h3>
            <p>
  {newsState.status === "loading"
    ? "Loading authenticated news..."
    : newsState.status === "error"
      ? "Authenticated news unavailable"
      : latestNewsArticle?.headline ||
        "No recent authenticated news found"}
</p>
          </article>

          <article className="askNinaIntelCard">
  <span>MARKET NEWS</span>
  <h3>Stock Market News</h3>
  <p>
    {marketNewsState.status === "loading"
      ? "Loading authenticated market news..."
      : marketNewsState.status === "error"
        ? "Authenticated market news unavailable"
        : latestMarketNewsArticle?.headline ||
          "No recent authenticated market news found"}
  </p>
</article>
        </div>
      </section>
    </section>
  );
}

export default AskNinaWorkspace;
